import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LAB_STATUSES,
  LAB_INTERPRETATIONS,
  type AnalyteKey,
  type AnalyteUnit,
} from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table } from '../components/ui.js';
import { Field, inputClass } from '../components/ui.js';
import { Select, Textarea, QueryState, ErrorState } from '../components/form.js';
import { TrendChart, type TrendPoint } from '../components/charts.js';
import { LabImport, labImportStrings } from '../components/LabImport.js';
import { downloadFile } from '../lib/api.js';

interface ApiAnalyte {
  id: string;
  key: AnalyteKey;
  customLabel: string | null;
  value: string;
  unit: AnalyteUnit;
  customUnit: string | null;
  referenceLow: string | null;
  referenceHigh: string | null;
  abnormal: boolean | null;
}

/** Display unit: the report's printed unit when the canonical unit is 'other'. */
function unitLabel(unit: string, customUnit?: string | null): string {
  if (unit === 'other' && customUnit) return customUnit;
  return unit.replace('per_mm3', '/mm³');
}
interface LabResultRow {
  id: string;
  sampleDate: string;
  resultDate: string | null;
  status: string;
  interpretation: string | null;
  interpretationNote: string | null;
  signedAt: string | null;
  analytes: ApiAnalyte[];
}
interface LabOrderRow {
  id: string;
  kind: string;
  sampleDate: string;
  status: string;
  sentAbroad: boolean;
  resultDate: string | null;
  note: string | null;
  flags: { pendingTooLong: boolean; redrawNeeded: boolean; daysPending: number | null };
}
interface TrendSeries {
  date: string;
  value: string;
  unit: string;
  customUnit: string | null;
  referenceLow: string | null;
  referenceHigh: string | null;
  abnormal: boolean | null;
  /** False for parent-submitted results not yet clinician-verified. */
  verified: boolean;
}

// NFS preset rows the create form starts with.
const NFS_PRESET: { key: AnalyteKey; unit: AnalyteUnit }[] = [
  { key: 'hemoglobin', unit: 'g/dL' },
  { key: 'wbc', unit: '10e3/uL' },
  { key: 'neutrophils_abs', unit: 'per_mm3' },
  { key: 'platelets', unit: '10e3/uL' },
];

// Analytes charted as trends.
const TREND_ANALYTES: { key: AnalyteKey; color: string }[] = [
  { key: 'hemoglobin', color: '#dc2626' },
  { key: 'neutrophils_abs', color: '#2563eb' },
  { key: 'platelets', color: '#7c3aed' },
  { key: 'wbc', color: '#0891b2' },
];

function num(s: string | null | undefined): number | null {
  if (s == null || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function Labs() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const resultsQ = useQuery({ queryKey: ['labs', 'results'], queryFn: () => api.get<{ results: LabResultRow[] }>('/labs/results') });
  const trendsQ = useQuery({ queryKey: ['labs', 'trends'], queryFn: () => api.get<{ series: Record<string, TrendSeries[]> }>('/labs/trends') });
  const ordersQ = useQuery({ queryKey: ['labs', 'orders'], queryFn: () => api.get<{ orders: LabOrderRow[] }>('/labs/orders') });

  const signM = useMutation({
    mutationFn: (id: string) => api.post(`/labs/results/${id}/sign`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labs', 'results'] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-800">{t('exam.nfs')}</h1>
        <Button onClick={() => setShowForm((s) => !s)}>
          {showForm ? t('common.cancel') : `+ ${t('exam.nfs')}`}
        </Button>
      </div>

      {showForm && <LabResultForm onDone={() => setShowForm(false)} />}

      {/* --- Import a lab report --- */}
      <Card title={labImportStrings.title[locale === 'en' ? 'en' : 'fr']}>
        <LabImport
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['labs', 'results'] });
            void qc.invalidateQueries({ queryKey: ['labs', 'trends'] });
            void qc.invalidateQueries({ queryKey: ['labs', 'verify-queue'] });
            void qc.invalidateQueries({ queryKey: ['alerts'] });
          }}
        />
      </Card>

      {/* --- Pending results to verify --- */}
      <VerifyQueue />

      {/* --- Trend charts --- */}
      <Card title={t('analyte.neutrophils_abs')}>
        <QueryState
          isLoading={trendsQ.isLoading}
          isError={trendsQ.isError}
          error={trendsQ.error}
          errorFallback={t('common.error')}
          isEmpty={Object.keys(trendsQ.data?.series ?? {}).length === 0}
          emptyMessage="—"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {TREND_ANALYTES.map(({ key, color }) => {
              // Only clinician-verified points belong on the clinical trend —
              // unverified parent imports are surfaced in the verify queue.
              const raw = (trendsQ.data?.series[key] ?? []).filter((p) => p.verified);
              if (raw.length === 0) return null;
              const unit = raw[0]?.unit;
              const points: TrendPoint[] = raw.map((p) => ({
                date: p.date,
                value: num(p.value) ?? 0,
                referenceLow: num(p.referenceLow),
                referenceHigh: num(p.referenceHigh),
              }));
              const refLow = num(raw[raw.length - 1]?.referenceLow);
              const refHigh = num(raw[raw.length - 1]?.referenceHigh);
              return (
                <div key={key} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="font-medium text-slate-700">{t(`analyte.${key}`)}</h3>
                    <span className="text-xs text-slate-400">{unit ? unit.replace('per_mm3', '/mm³') : ''}</span>
                  </div>
                  <TrendChart data={points} unit={unit} color={color} referenceLow={refLow} referenceHigh={refHigh} />
                  <Table headers={[t('weekly.weekOf'), '', t('analyte.custom')]}>
                    {raw.map((p, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1 pr-4">{p.date}</td>
                        <td className={`py-1 pr-4 ${p.abnormal ? 'text-danger font-semibold' : ''}`}>
                          {p.value} {unitLabel(p.unit, p.customUnit)}
                          {p.abnormal && ' ⚠'}
                        </td>
                        <td className="py-1 pr-4 text-slate-400 text-xs">
                          {p.referenceLow ?? '—'}–{p.referenceHigh ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </Table>
                </div>
              );
            })}
          </div>
        </QueryState>
      </Card>

      {/* --- Results list --- */}
      <Card title={t('lab.received')}>
        <QueryState
          isLoading={resultsQ.isLoading}
          isError={resultsQ.isError}
          error={resultsQ.error}
          errorFallback={t('common.error')}
          isEmpty={(resultsQ.data?.results.length ?? 0) === 0}
          emptyMessage="—"
        >
          <div className="space-y-4">
            {resultsQ.data?.results.map((r) => (
              <div key={r.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <strong>{r.sampleDate}</strong>
                    {r.resultDate && <span className="text-slate-400">→ {r.resultDate}</span>}
                    <Badge tone={r.status === 'reviewed' ? 'ok' : r.status === 'redraw_needed' ? 'danger' : 'info'}>
                      {t(`lab.${r.status}`)}
                    </Badge>
                    {r.interpretation && <Badge tone="warn">{t(`interp.${r.interpretation}`)}</Badge>}
                  </div>
                  {r.status !== 'reviewed' && (
                    <Button variant="secondary" onClick={() => signM.mutate(r.id)} disabled={signM.isPending}>
                      {t('common.sign')}
                    </Button>
                  )}
                </div>
                {r.interpretationNote && <p className="text-sm text-slate-500 mb-2">{r.interpretationNote}</p>}
                <Table headers={[t('analyte.custom'), '', '']}>
                  {r.analytes.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100">
                      <td className="py-1 pr-4">{a.customLabel || t(`analyte.${a.key}`)}</td>
                      <td className={`py-1 pr-4 ${a.abnormal ? 'text-danger font-semibold' : 'text-slate-700'}`}>
                        {a.value} {unitLabel(a.unit, a.customUnit)}
                        {a.abnormal && ' ⚠'}
                      </td>
                      <td className="py-1 pr-4 text-slate-400 text-xs">
                        {a.referenceLow ?? '—'}–{a.referenceHigh ?? '—'}
                      </td>
                    </tr>
                  ))}
                </Table>
              </div>
            ))}
          </div>
        </QueryState>
        {signM.isError && <div className="mt-2"><ErrorState error={signM.error} fallback={t('common.error')} /></div>}
      </Card>

      {/* --- Lab orders --- */}
      <LabOrders ordersQ={ordersQ} />
    </div>
  );
}

function LabResultForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [sampleDate, setSampleDate] = useState(today);
  const [resultDate, setResultDate] = useState('');
  const [status, setStatus] = useState('received');
  const [interpretation, setInterpretation] = useState('');
  const [interpretationNote, setInterpretationNote] = useState('');
  const [rows, setRows] = useState(
    NFS_PRESET.map((p) => ({ key: p.key, unit: p.unit, value: '', referenceLow: '', referenceHigh: '' })),
  );

  function setRow(i: number, patch: Partial<(typeof rows)[number]>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  const m = useMutation({
    mutationFn: () =>
      api.post('/labs/results', {
        sampleDate,
        resultDate: resultDate || null,
        status,
        isNfsPreset: true,
        interpretation: interpretation || undefined,
        interpretationNote: interpretationNote || undefined,
        analytes: rows
          .filter((r) => r.value.trim() !== '')
          .map((r) => ({
            key: r.key,
            value: r.value.trim(),
            unit: r.unit,
            referenceLow: r.referenceLow.trim() || null,
            referenceHigh: r.referenceHigh.trim() || null,
          })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labs'] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      onDone();
    },
  });

  return (
    <Card title={`+ ${t('exam.nfs')}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4">
          <Field label={t('lab.sampleDate')}>
            <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
          </Field>
          <Field label={t('lab.resultDate')}>
            <input className={inputClass} type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)} />
          </Field>
          <Field label={t('lab.received')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              {LAB_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`lab.${s}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('interp.no_change')}>
            <Select value={interpretation} onChange={(e) => setInterpretation(e.target.value)}>
              <option value="">—</option>
              {LAB_INTERPRETATIONS.map((s) => (
                <option key={s} value={s}>{t(`interp.${s}`)}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Table headers={[t('analyte.custom'), t('common.value'), t('common.unit'), t('lab.refLow'), t('lab.refHigh')]}>
          {rows.map((r, i) => (
            <tr key={r.key} className="border-b border-slate-100">
              <td className="py-1 pr-4 font-medium">{t(`analyte.${r.key}`)}</td>
              <td className="py-1 pr-2">
                <input className={inputClass} inputMode="decimal" value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} />
              </td>
              <td className="py-1 pr-2 text-xs text-slate-500">{r.unit.replace('per_mm3', '/mm³')}</td>
              <td className="py-1 pr-2">
                <input className={inputClass} inputMode="decimal" value={r.referenceLow} onChange={(e) => setRow(i, { referenceLow: e.target.value })} />
              </td>
              <td className="py-1 pr-2">
                <input className={inputClass} inputMode="decimal" value={r.referenceHigh} onChange={(e) => setRow(i, { referenceHigh: e.target.value })} />
              </td>
            </tr>
          ))}
        </Table>

        <Field label={t('weekly.observations')}>
          <Textarea rows={2} value={interpretationNote} onChange={(e) => setInterpretationNote(e.target.value)} />
        </Field>

        {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
        <div className="flex gap-2">
          <Button type="submit" disabled={m.isPending}>{t('common.save')}</Button>
          <Button type="button" variant="secondary" onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// --- Verify queue (pending imported results awaiting a clinician signature) ---
interface VerifyAnalyte {
  key: AnalyteKey;
  value: string;
  unit: AnalyteUnit;
  customUnit: string | null;
  referenceLow: string | null;
  referenceHigh: string | null;
  abnormal: boolean | null;
}
interface VerifyResultRow {
  id: string;
  sampleDate: string;
  resultDate: string | null;
  needsVerification: boolean;
  sourceAttachmentId: string | null;
  analytes: VerifyAnalyte[];
}

function VerifyQueue() {
  const { t } = useI18n();
  const qc = useQueryClient();

  const queueQ = useQuery({
    queryKey: ['labs', 'verify-queue'],
    queryFn: () => api.get<{ results: VerifyResultRow[] }>('/labs/verify-queue'),
  });

  return (
    <Card title={t('lab.toVerify')}>
      <QueryState
        isLoading={queueQ.isLoading}
        isError={queueQ.isError}
        error={queueQ.error}
        errorFallback={t('common.error')}
        isEmpty={(queueQ.data?.results.length ?? 0) === 0}
        emptyMessage={t('lab.nothingToVerify')}
      >
        <div className="space-y-4">
          {queueQ.data?.results.map((r) => (
            <VerifyRow key={r.id} row={r} />
          ))}
        </div>
      </QueryState>
    </Card>
  );

  function VerifyRow({ row }: { row: VerifyResultRow }) {
    const [analytes, setAnalytes] = useState(
      row.analytes.map((a) => ({
        key: a.key,
        unit: a.unit,
        customUnit: a.customUnit ?? '',
        value: a.value,
        referenceLow: a.referenceLow ?? '',
        referenceHigh: a.referenceHigh ?? '',
      })),
    );

    function setA(i: number, patch: Partial<(typeof analytes)[number]>) {
      setAnalytes((as) => as.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
    }

    const verifyM = useMutation({
      mutationFn: () =>
        api.post(`/labs/results/${row.id}/verify`, {
          analytes: analytes
            .filter((a) => a.value.trim() !== '')
            .map((a) => ({
              key: a.key,
              value: a.value.trim(),
              unit: a.unit,
              customUnit: a.unit === 'other' && a.customUnit.trim() ? a.customUnit.trim() : undefined,
              referenceLow: a.referenceLow.trim() || null,
              referenceHigh: a.referenceHigh.trim() || null,
            })),
        }),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['labs', 'verify-queue'] });
        void qc.invalidateQueries({ queryKey: ['labs', 'results'] });
        void qc.invalidateQueries({ queryKey: ['labs', 'trends'] });
        void qc.invalidateQueries({ queryKey: ['alerts'] });
      },
    });

    return (
      <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm">
            <strong>{row.sampleDate}</strong>
            {row.resultDate && <span className="text-slate-400">→ {row.resultDate}</span>}
            <Badge tone="warn">{t('lab.pending')}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {row.sourceAttachmentId && (
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  void downloadFile(`/attachments/${row.sourceAttachmentId}/download`, 'rapport')
                }
              >
                {t('lab.viewScan')}
              </Button>
            )}
            <Button type="button" onClick={() => verifyM.mutate()} disabled={verifyM.isPending}>
              {t('lab.verifySign')}
            </Button>
          </div>
        </div>

        <Table headers={[t('analyte.custom'), t('common.value'), t('common.unit'), t('lab.refLow'), t('lab.refHigh')]}>
          {analytes.map((a, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-1 pr-4 font-medium">{t(`analyte.${a.key}`)}</td>
              <td className="py-1 pr-2">
                <input className={inputClass} inputMode="decimal" value={a.value} onChange={(e) => setA(i, { value: e.target.value })} />
              </td>
              <td className="py-1 pr-2 text-xs text-slate-500">{unitLabel(a.unit, a.customUnit)}</td>
              <td className="py-1 pr-2">
                <input className={inputClass} inputMode="decimal" value={a.referenceLow} onChange={(e) => setA(i, { referenceLow: e.target.value })} />
              </td>
              <td className="py-1 pr-2">
                <input className={inputClass} inputMode="decimal" value={a.referenceHigh} onChange={(e) => setA(i, { referenceHigh: e.target.value })} />
              </td>
            </tr>
          ))}
        </Table>

        {verifyM.isError && <div className="mt-2"><ErrorState error={verifyM.error} fallback={t('common.error')} /></div>}
      </div>
    );
  }
}

function LabOrders({ ordersQ }: { ordersQ: ReturnType<typeof useQuery<{ orders: LabOrderRow[] }>> }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [sampleDate, setSampleDate] = useState(today);
  const [sentAbroad, setSentAbroad] = useState(false);
  const [note, setNote] = useState('');

  const createM = useMutation({
    mutationFn: () => api.post('/labs/orders', { kind: 'nfs', sampleDate, status: 'ordered', sentAbroad, note: note || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labs', 'orders'] });
      setNote('');
    },
  });
  const statusM = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.put(`/labs/orders/${id}`, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labs', 'orders'] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  return (
    <Card title={t('lab.ordered')}>
      <form
        className="flex flex-wrap items-end gap-3 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          createM.mutate();
        }}
      >
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">{t('action.scheduleLab')}</span>
          <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
        </label>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" checked={sentAbroad} onChange={(e) => setSentAbroad(e.target.checked)} />
          {t('lab.sentAbroad')}
        </label>
        <input className={`${inputClass} flex-1 min-w-40`} placeholder={t('weekly.observations')} value={note} onChange={(e) => setNote(e.target.value)} />
        <Button type="submit" disabled={createM.isPending}>+ {t('lab.ordered')}</Button>
      </form>

      <QueryState
        isLoading={ordersQ.isLoading}
        isError={ordersQ.isError}
        error={ordersQ.error}
        errorFallback={t('common.error')}
        isEmpty={(ordersQ.data?.orders.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={[t('lab.sampleDate'), t('lab.received'), t('lab.flags'), '']}>
          {ordersQ.data?.orders.map((o) => (
            <tr key={o.id} className="border-b border-slate-100">
              <td className="py-2 pr-4">
                {o.sampleDate} {o.sentAbroad && <span className="text-xs text-slate-400">({t('lab.abroad')})</span>}
              </td>
              <td className="py-2 pr-4">
                <Badge tone={o.status === 'received' ? 'ok' : o.status === 'redraw_needed' ? 'danger' : 'info'}>{t(`lab.${o.status}`)}</Badge>
              </td>
              <td className="py-2 pr-4 space-x-1">
                {o.flags.pendingTooLong && <Badge tone="warn">{t('rule.lab.pendingTooLong', { daysPending: String(o.flags.daysPending ?? 0) })}</Badge>}
                {o.flags.redrawNeeded && <Badge tone="danger">{t('rule.lab.redrawNeeded')}</Badge>}
              </td>
              <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                <button className="text-xs underline text-brand" onClick={() => statusM.mutate({ id: o.id, status: 'received' })}>
                  {t('lab.received')}
                </button>
                <button className="text-xs underline text-amber-700" onClick={() => statusM.mutate({ id: o.id, status: 'pending' })}>
                  {t('lab.pending')}
                </button>
                <button className="text-xs underline text-danger" onClick={() => statusM.mutate({ id: o.id, status: 'redraw_needed' })}>
                  {t('lab.redraw_needed')}
                </button>
              </td>
            </tr>
          ))}
        </Table>
      </QueryState>
      {createM.isError && <div className="mt-2"><ErrorState error={createM.error} fallback={t('common.error')} /></div>}
    </Card>
  );
}
